import { ChangeDetectionStrategy, Component, ElementRef, input, viewChild } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { SignalStringFieldBaseDirective } from '../signal-field-base.directive';

/**
 * Low-level date input for signal forms.
 *
 * Everything store- and validation-related sits in `SignalStringFieldBaseDirective`; this
 * class adds only the native picker bounds and the calendar icon. Wiring a field
 * needs no selector, no action and no per-field plumbing:
 *
 * ```html
 * <pace-core-date-field [formField]="signalForm.dateValue" ariaLabel="Start date" />
 * ```
 *
 * The value is the HTML5 `yyyy-MM-dd` string the native input works with (empty
 * when unset) rather than a `moment`, which keeps any form model built from these
 * fields directly serializable into an NgRx slice.
 *
 * The bounds need inputs of their own because the `FormUiControl` contract types
 * `min`/`max` as numbers, which does not fit a date string — unlike the length and
 * pattern constraints on `pace-core-text-field`, which arrive through the contract.
 */
@Component({
    selector: 'pace-date-field',
    templateUrl: './date-field.component.html',
    styleUrls: ['./date-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent],
})
export class DateFieldComponent extends SignalStringFieldBaseDirective {
    /** HTML5 date (`yyyy-MM-dd`) the native picker is limited to; empty for no limit */
    public readonly minDate = input('');
    public readonly maxDate = input('');

    public readonly faCalendar = faCalendar;

    private readonly dateInput = viewChild.required<ElementRef<HTMLInputElement>>('dateInput');

    public openCalendar(): void {
        if (!this.readonly() && !this.disabled()) {
            this.dateInput().nativeElement.showPicker();
        }
    }
}
